import React, { useEffect, useState } from "react";
import { bool, func } from "prop-types";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import Button from "pages/_components/Button";
import GridTable from "pages/_components/GridTable/GridTable";
import ProductList from "pages/_components/product/List";
import Drawer from "pages/_components/Drawer/Drawer";
import { actions as notificationActions } from "reducers/notification";
import { useEcheckTabs } from "pages/echeck/hooks/useEcheckTabs";
import { checkDisableMultiple, echeqOptions } from "util/echecks";
import * as config from "util/config";
import * as i18n from "util/i18n";
import Image from "pages/_components/Image";
import { isIOS, isMobile } from "react-device-detect";
import I18n from "pages/_components/I18n";
import TitleColumns from "./components/TitleColumns";
import ReceivedEcheckItem from "./components/ItemRow";
import ReceivedSideBarForm from "./components/SideBarForm";
import FilterTags from "../components/FilterTags";
import MultipleActionSelector from "./components/MultipleActionSelector";
import EcheqTotalizer from "../../components/EcheqTotalizer";

const ReceivedEchecks = ({ isDesktop, fetchMoreTransactions }) => {
    const {
        echeqList = [],
        fetching,
        hasMoreData,
        environmentCuit,
        echeqFilters: filters,
        echeqPagination,
    } = useSelector((state) => state.echecks);
    const [openSidebar, setOpenSidebar] = useState(false);
    const [actionDrawer, setActionDrawer] = useState({ showDrawer: false, contentDrawer: null });
    const [selectedRows, setSelectedRows] = useState([]);
    const [selectedCurrency, setSelectedCurrency] = useState(null);
    const [, setSelectedAll] = useState(false);
    const { dispatch } = useEcheckTabs(0);

    useEffect(() => {
        setSelectedRows([]);
        setSelectedCurrency(null);
    }, [filters]);

    const addSelectedRow = (echeck) => {
        const maxSelectedReached = selectedRows.length >= config.getInteger("echeck.multiple.selection.max", 10);
        const alreadySelected = selectedRows.find((element) => element.cheque_numero === echeck.cheque_numero);
        const okToAdd = !checkDisableMultiple(echeck, environmentCuit, filters.status);

        if (!okToAdd) return;

        // Si ya está seleccionado, deseleccionar
        if (alreadySelected) {
            const newSelectedRows = selectedRows.filter((element) => element.cheque_numero !== echeck.cheque_numero);
            setSelectedRows(newSelectedRows);
            // Resetear la moneda si no quedan elementos seleccionados
            if (newSelectedRows.length === 0) {
                setSelectedCurrency(null);
            }
            setSelectedAll(false);
            return;
        }

        // Validar que la moneda sea igual a la ya seleccionada
        const hasDifferentCurrency = selectedCurrency && selectedCurrency !== echeck.moneda;

        if (hasDifferentCurrency) {
            dispatch(
                notificationActions.showNotification(
                    i18n.get("echeck.multiple.selection.same.issuer.currency"),
                    "warning",
                    ["echecks"]
                )
            );
            return;
        }

        if (maxSelectedReached) {
            dispatch(
                notificationActions.showNotification(i18n.get("echeck.multiple.selection.max"), "warning", [
                    "echecks",
                ]),
            );
            return;
        }

        // Agregar a seleccionados
        setSelectedRows([...selectedRows, echeck]);
        // Establecer la moneda si es la primera selección
        if (!selectedCurrency) {
            setSelectedCurrency(echeck.moneda);
        }
        setSelectedAll(false);
    };

    const selectAll = (checked) => {
        let listElements = checked ? selectedRows : [];
        let hasDifferentCurrency = false;
        let hasReachedMax = false;

        if (checked && selectedRows.length >= config.getInteger("echeck.multiple.selection.max", 10)) {
            listElements = [];
        } else if (checked) {
            let currencyToUse = selectedCurrency;
            
            for (let i = 0; i < echeqList?.length; i++) {
                const okToAdd = !checkDisableMultiple(echeqList[i], environmentCuit, filters.status);
                
                if (!okToAdd) continue;
                
                const maxSelectedReached =
                    listElements.length >= config.getInteger("echeck.multiple.selection.max", 10);

                if (maxSelectedReached) {
                    hasReachedMax = true;
                    break;
                }

                // Ya está seleccionado, saltar
                if (selectedRows.some((echeq) => echeq.cheque_id === echeqList[i].cheque_id)) {
                    continue;
                }

                // Determinar moneda base
                if (!currencyToUse && listElements.length === 0) {
                    currencyToUse = echeqList[i].moneda;
                }

                // Validar que tenga la misma moneda
                const hasSameCurrency = echeqList[i].moneda === currencyToUse;

                if (!hasSameCurrency) {
                    hasDifferentCurrency = true;
                    continue;
                }

                listElements = [...listElements, echeqList[i]];
            }

            // Mostrar notificaciones después del loop
            if (hasDifferentCurrency) {
                dispatch(
                    notificationActions.showNotification(
                        i18n.get("echeck.multiple.selection.same.issuer.currency"),
                        "warning",
                        ["echecks"]
                    )
                );
            }
            if (hasReachedMax) {
                dispatch(
                    notificationActions.showNotification(i18n.get("echeck.multiple.selection.max"), "warning", [
                        "echecks",
                    ]),
                );
            }
        }

        setSelectedRows(listElements);
        // Resetear la moneda si se deseleccionan todos
        if (!checked || listElements.length === 0) {
            setSelectedCurrency(null);
        } else if (listElements.length > 0) {
            setSelectedCurrency(listElements[0].moneda);
        }
        setSelectedAll(checked && listElements.length >= config.getInteger("echeck.multiple.selection.max", 10));
    };

    const allChecksAreChecked = () => {
        // Obtener todos los echeqs seleccionables
        const selectableEcheqs = echeqList.filter(
            (echeq) => !checkDisableMultiple(echeq, environmentCuit, filters.status)
        );

        // Si no hay echeqs seleccionables, retornar false
        if (selectableEcheqs.length === 0) return false;

        // Verificar que TODOS los echeqs seleccionables estén en selectedRows
        return selectableEcheqs.every((echeq) =>
            selectedRows.some((selected) => selected.cheque_id === echeq.cheque_id)
        );
    };

    const openSideBarFilters = () => {
        setOpenSidebar(true);
    };

    const handleCloseActionDrawer = () => {
        setActionDrawer({ showDrawer: false, contentDrawer: null });
    };

    const calculateAmount = () => {
        // const { echeqsToEmit } = this.props;
        if (selectedRows.length > 1) {
            return selectedRows.reduce((a, b) => a + b.monto, 0);
        }

        return 0;
    };

    return (
        <div className="container pb-8 echeq-table-wrapper">
            <FilterTags openSideBarFilters={openSideBarFilters} optionEcheq={echeqOptions[0]} />
            <MultipleActionSelector
                selectedRows={selectedRows}
                environmentCuit={environmentCuit}
                echeqsListTotal={echeqList?.length}
                setDrawer={setActionDrawer}
            />
            {echeqList?.length === 0 && !fetching ? (
                <div className="box info service-payments__no-data">
                    <Image src="images/information_fill.svg" /> <I18n id="echecks.service.noReceived" />
                </div>
            ) : (
                <>
                    <ProductList
                        fetchingWithPreviousData
                        fetching={fetching}
                        items={echeqList || []}
                        noDataText="global.no.results"
                        noFiltersDataText="global.no.results"
                        renderItem={(item) => (
                            <GridTable.Container
                                key={`received-echeck-item-${item.cheque_numero}`}
                                renderAs={Link}
                                to={`/echecks/received/${item.cheque_id}`}
                                className={!isDesktop && "box box-shadow"}>
                                <ReceivedEcheckItem
                                    receivedEcheck={item}
                                    selectedRows={selectedRows}
                                    addSelectedRow={addSelectedRow}
                                    setDrawer={setActionDrawer}
                                    isDesktop={isDesktop}
                                />
                            </GridTable.Container>
                        )}>
                        {(listItem) => (
                            <GridTable className="echeq-table tablelist received">
                                {isDesktop && (
                                    <GridTable.Header>
                                        <GridTable.Container>
                                            <TitleColumns selectAll={selectAll} allChecksAreChecked={allChecksAreChecked} />
                                        </GridTable.Container>
                                    </GridTable.Header>
                                )}
                                <GridTable.Body>{listItem}</GridTable.Body>
                                {hasMoreData && (
                                    <Button
                                        bsStyle="link"
                                        onClick={() => fetchMoreTransactions(echeqPagination, filters, setSelectedAll)}
                                        image="images/figma/icon/arrow.svg"
                                        label="global.showMore"
                                        className="view-more"
                                    />
                                )}
                            </GridTable>
                        )}
                    </ProductList>
                    { selectedRows.length > 1 && (
                        <EcheqTotalizer
                            selectedCount={selectedRows.length}
                            totalAmount={calculateAmount()}
                            selectedCurrency={selectedCurrency}
                            isDesktop={!isMobile}
                        />
                    )}
                </>
            )}
            <Drawer
                openSidebar={openSidebar}
                toggleSidebar={setOpenSidebar}
                isFullHeight={isMobile || isIOS}
                enableCloseButton={!!isDesktop}>
                <ReceivedSideBarForm closeSidebar={setOpenSidebar} />
            </Drawer>
            <Drawer openSidebar={actionDrawer.showDrawer} onClose={handleCloseActionDrawer}>
                {actionDrawer.contentDrawer}
            </Drawer>
        </div>
    );
};

ReceivedEchecks.propTypes = {
    isDesktop: bool.isRequired,
    fetchMoreTransactions: func.isRequired,
};

export default ReceivedEchecks;
